import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiChevronDown, FiX } from "react-icons/fi";
import { ReactComponent as LayersIcon } from "../../assets/icons/layers-icon.svg";
import { fetchDatasetsForCurrentUser } from "../../store/datasetSlice";
import { updateProject } from "../../store/projectSlice";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import type { AppDispatch, RootState } from "../../store";

interface WidgetDataset {
  id: string;
  name: string;
  datasetType: string;
  updateFrequency?: string;
  parameterType?: string;
  units?: string;
  latestForecastIssueTime?: string | null; // TODO(dead-code #994): the field is gone from Dataset
}

interface DatasetsWidgetProps {
  projectId: string;
}

const TYPE_STYLES: Record<string, string> = {
  HISTORICAL: "bg-blue-100 text-blue-800",
  FORECAST: "bg-green-100 text-green-800",
  ENSEMBLE_FORECAST: "bg-purple-100 text-purple-800",
};

const TYPE_LABELS: Record<string, string> = {
  HISTORICAL: "HIST",
  FORECAST: "FCST",
  ENSEMBLE_FORECAST: "ENS",
};

const DatasetsWidget: React.FC<DatasetsWidgetProps> = ({ projectId }) => {
  const dispatch = useDispatch<AppDispatch>();

  const personalDatasets = useSelector((state: RootState) => state.datasets.personalDatasets) as Record<string, WidgetDataset>;
  const loading = useSelector((state: RootState) => state.datasets.loading);
  const project = useSelector((state: RootState) => state.projects.projects.find((p) => p.id === projectId));

  const attachedIds = project?.datasetIds ?? [];

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading === "idle") dispatch(fetchDatasetsForCurrentUser());
  }, [dispatch, loading]);

  // Close the dropdown when clicking outside it
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const availableDatasets = Object.values(personalDatasets || {}) as WidgetDataset[];
  // Only the datasets the user has attached to this project appear in the list
  const datasets = availableDatasets.filter((d) => attachedIds.includes(d.id));

  const persistAttached = (datasetIds: string[]) => {
    dispatch(updateProject({ id: projectId, datasetIds }));
  };

  const toggleAttached = (datasetId: string) => {
    const next = attachedIds.includes(datasetId)
      ? attachedIds.filter((id) => id !== datasetId)
      : [...attachedIds, datasetId];
    persistAttached(next);
  };

  const handleRemove = (e: React.MouseEvent, datasetId: string) => {
    e.stopPropagation();
    persistAttached(attachedIds.filter((id) => id !== datasetId));
  };

  // Open the graph in a modal — the modal owns the data fetch and render states
  const handleRowClick = (dataset: WidgetDataset) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.datasetGraph,
      properties: { __modalSize: "xl", dataset },
    }));
  };

  const renderDropdown = () => (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen((v) => !v)}
        className="eai-btn-grn text-sm flex items-center gap-1"
      >
        Add datasets
        <FiChevronDown size={13} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 mt-1 w-64 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-20 p-1">
          {availableDatasets.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No datasets available</p>
          ) : (
            availableDatasets.map((dataset) => {
              const checked = attachedIds.includes(dataset.id);
              return (
                <button
                  key={dataset.id}
                  onClick={() => toggleAttached(dataset.id)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 text-left transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    className="flex-shrink-0 accent-eai-primary-accent"
                  />
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${TYPE_STYLES[dataset.datasetType] || "bg-gray-100 text-gray-700"}`}>
                    {TYPE_LABELS[dataset.datasetType] || dataset.datasetType}
                  </span>
                  <span className="text-sm text-gray-900 truncate flex-1">{dataset.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden flex flex-col h-[17.5rem] lg:h-auto lg:flex-1 lg:min-h-0">
      {/* Header */}
      <div className="flex-none flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <LayersIcon className="w-4 h-4 text-eai-primary-accent" />
          Datasets
        </div>
        {renderDropdown()}
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {(loading === "idle" || loading === "pending") && availableDatasets.length === 0 ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-eai-primary-accent border-t-transparent" />
          </div>
        ) : datasets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-4">
            <LayersIcon className="w-8 h-8 text-gray-200" />
            <p className="text-sm text-gray-400 text-center">No datasets added to this project</p>
            <button onClick={() => setDropdownOpen(true)} className="eai-btn-grn text-sm">
              Add datasets
            </button>
          </div>
        ) : (
          <ul className="p-2 flex flex-col gap-1">
            {datasets.map((dataset) => (
              <li key={dataset.id}>
                {/* Row — opens the graph in a modal */}
                <div
                  role="button"
                  tabIndex={0}
                  className="group flex items-center gap-3 px-2 py-2 mx-2 border border-transparent rounded-md cursor-pointer transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-eai-primary-accent"
                  onClick={() => handleRowClick(dataset)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleRowClick(dataset);
                    }
                  }}
                >
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${TYPE_STYLES[dataset.datasetType] || "bg-gray-100 text-gray-700"}`}
                  >
                    {TYPE_LABELS[dataset.datasetType] || dataset.datasetType}
                  </span>
                  <span className="text-sm font-medium text-gray-900 truncate flex-1">
                    {dataset.name}
                  </span>
                  {dataset.parameterType && (
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {dataset.parameterType}
                    </span>
                  )}
                  <button
                    onClick={(e) => handleRemove(e, dataset.id)}
                    className="p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50 active:text-red-600 md:opacity-0 md:group-hover:opacity-100 transition flex-shrink-0"
                    title="Remove from project"
                  >
                    <FiX size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DatasetsWidget;
